// src/components/layout/Navigation.js (반응형 - 모바일 햄버거 메뉴 적용)
import React, { useState, useEffect } from 'react';
import { ChevronDown, Menu, X } from 'lucide-react';

const EXPERTISE_ITEMS = [
  { id: 'fuel-cell', name: 'Fuel Cell Driven Smart Farm' },
  { id: 'digital-twin-expertise', name: 'Digital Twin for Remote Cultivation' },
  { id: 'recipe-commercialization', name: 'Cultivation Recipe Commercialization' }
];

const SOLUTION_ITEMS = [
  { id: 'digital-twin-solution', name: 'Digital Twin for Remote Cultivation' },
  { id: 'ai-recipe', name: 'AI based Cultivation Recipe' },
  { id: 'image-analysis', name: 'Image Analysis Tool' }
];

const MOBILE_BREAKPOINT = 768;

const Navigation = ({ currentPage, setCurrentPage, onContactClick }) => {
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_BREAKPOINT : false
  );

  // 화면 폭이 바뀔 때마다 모바일/PC 레이아웃 전환
  useEffect(() => {
    const handleResize = () => {
      const nowMobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobile(nowMobile);
      if (!nowMobile) {
        setMobileMenuOpen(false);
        setMobileSection(null);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleMenuClick = (pageId) => {
    setCurrentPage(pageId);
    setDropdownOpen(null);
    setMobileMenuOpen(false);
    setMobileSection(null);
  };

  const handleContactClick = () => {
    onContactClick();
    setMobileMenuOpen(false);
    setMobileSection(null);
  };

  const toggleMobileSection = (section) => {
    setMobileSection(mobileSection === section ? null : section);
  };

  // 순수 CSS 스타일 정의
  const styles = {
    nav: {
      backgroundColor: 'white',
      color: '#4B5563',
      padding: '1rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      borderBottom: '1px solid #E5E7EB',
      fontFamily: 'Arial, sans-serif'
    },
    container: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      maxWidth: '1200px',
      margin: '0 auto',
      width: '100%'
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      transition: 'transform 0.2s ease'
    },
    logoImage: {
      width: '150px',
      height: '35px',
      objectFit: 'contain',
      marginRight: '0'
    },
    menuContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '2rem'
    },
    menuItem: {
      position: 'relative',
      display: 'inline-block'
    },
    menuButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.75rem 1rem',
      backgroundColor: 'transparent',
      border: 'none',
      color: '#4B5563',
      fontSize: '1rem',
      cursor: 'pointer',
      borderRadius: '0.375rem',
      transition: 'background-color 0.2s ease',
      fontFamily: 'inherit'
    },
    contactButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.75rem 1.5rem',
      backgroundColor: '#16a34a',
      border: 'none',
      color: 'white',
      fontSize: '1rem',
      cursor: 'pointer',
      borderRadius: '0.5rem',
      transition: 'background-color 0.2s ease',
      fontFamily: 'inherit'
    },
    dropdownContainer: {
      position: 'absolute',
      top: '100%',
      left: '0',
      paddingTop: '0.5rem',
      zIndex: 50
    },
    dropdown: {
      backgroundColor: '#F3F4F6',
      color: '#374151',
      borderRadius: '0.5rem',
      boxShadow: '0 10px 15px rgba(0, 0, 0, 0.1)',
      border: '1px solid #E5E7EB',
      minWidth: '320px',
      opacity: 0.95
    },
    dropdownItem: {
      display: 'block',
      width: '100%',
      textAlign: 'left',
      padding: '0.75rem 1rem',
      backgroundColor: 'transparent',
      border: 'none',
      color: '#374151',
      fontSize: '0.875rem',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontFamily: 'inherit'
    },
    hamburgerButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      backgroundColor: 'transparent',
      border: 'none',
      color: '#374151',
      cursor: 'pointer',
      borderRadius: '0.375rem'
    },
    mobilePanel: {
      width: '100%',
      marginTop: '0.75rem',
      borderTop: '1px solid #E5E7EB',
      paddingTop: '0.5rem',
      display: 'flex',
      flexDirection: 'column'
    },
    mobileTopButton: {
      width: '100%',
      textAlign: 'left',
      padding: '0.875rem 0.5rem',
      backgroundColor: 'transparent',
      border: 'none',
      color: '#374151',
      fontSize: '1rem',
      fontWeight: 500,
      cursor: 'pointer',
      fontFamily: 'inherit',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    mobileSubItem: {
      width: '100%',
      textAlign: 'left',
      padding: '0.75rem 1.5rem',
      backgroundColor: 'transparent',
      border: 'none',
      color: '#4B5563',
      fontSize: '0.9rem',
      cursor: 'pointer',
      fontFamily: 'inherit'
    },
    mobileContactButton: {
      width: '100%',
      textAlign: 'center',
      marginTop: '0.75rem',
      padding: '0.875rem',
      backgroundColor: '#16a34a',
      border: 'none',
      color: 'white',
      fontSize: '1rem',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      fontFamily: 'inherit'
    }
  };

  return (
    <nav style={styles.nav}>
      <div style={styles.container}>
        {/* 로고 */}
        <div
          style={styles.logo}
          onClick={() => handleMenuClick('home')}
          onMouseEnter={(e) => {
            if (!isMobile) e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <img
            src="/abocado_logo.png"
            alt="Abocado Logo"
            style={styles.logoImage}
            onError={(e) => {
              e.target.style.display = 'none';
              const fallback = document.createElement('span');
              fallback.innerHTML = '🌱 ABOCADO';
              fallback.style.fontSize = '1.5rem';
              fallback.style.fontWeight = 'bold';
              fallback.style.color = '#15803d';
              e.target.parentNode.appendChild(fallback);
            }}
          />
        </div>

        {/* PC: 가로 메뉴 / 모바일: 햄버거 버튼 */}
        {isMobile ? (
          <button
            style={styles.hamburgerButton}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="메뉴 열기"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        ) : (
          <div style={styles.menuContainer}>
            {/* Expertises Dropdown */}
            <div
              style={styles.menuItem}
              onMouseEnter={() => setDropdownOpen('expertise')}
              onMouseLeave={() => setDropdownOpen(null)}
            >
              <button
                style={styles.menuButton}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#F3F4F6'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <span>Expertises</span>
                <ChevronDown size={16} />
              </button>
              {dropdownOpen === 'expertise' && (
                <div style={styles.dropdownContainer}>
                  <div style={styles.dropdown}>
                    {EXPERTISE_ITEMS.map(item => (
                      <button
                        key={item.id}
                        style={styles.dropdownItem}
                        onClick={() => handleMenuClick(item.id)}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = 'white';
                          e.target.style.color = '#1d4ed8';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = '#374151';
                        }}
                      >
                        {item.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Solutions Dropdown */}
            <div
              style={styles.menuItem}
              onMouseEnter={() => setDropdownOpen('solutions')}
              onMouseLeave={() => setDropdownOpen(null)}
            >
              <button
                style={styles.menuButton}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#F3F4F6'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <span>Solutions</span>
                <ChevronDown size={16} />
              </button>
              {dropdownOpen === 'solutions' && (
                <div style={styles.dropdownContainer}>
                  <div style={styles.dropdown}>
                    {SOLUTION_ITEMS.map(item => (
                      <button
                        key={item.id}
                        style={styles.dropdownItem}
                        onClick={() => handleMenuClick(item.id)}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = 'white';
                          e.target.style.color = '#1d4ed8';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = '#374151';
                        }}
                      >
                        {item.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Business Collaboration */}
            <button
              style={styles.menuButton}
              onClick={() => handleMenuClick('business')}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#F3F4F6'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              Business Collaboration
            </button>

            {/* Registration */}
            <button
              style={styles.menuButton}
              onClick={() => handleMenuClick('registration')}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#F3F4F6'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              Registration
            </button>

            {/* About */}
            <button
              style={styles.menuButton}
              onClick={() => handleMenuClick('about')}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#F3F4F6'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              About
            </button>

            {/* Contact */}
            <button
              style={styles.contactButton}
              onClick={handleContactClick}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#15803d'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#16a34a'}
            >
              Contact
            </button>
          </div>
        )}
      </div>

      {/* 모바일 전용 펼침 메뉴 */}
      {isMobile && mobileMenuOpen && (
        <div style={styles.mobilePanel}>
          <button style={styles.mobileTopButton} onClick={() => toggleMobileSection('expertise')}>
            <span>Expertises</span>
            <ChevronDown size={16} style={{ transform: mobileSection === 'expertise' ? 'rotate(180deg)' : 'none' }} />
          </button>
          {mobileSection === 'expertise' && EXPERTISE_ITEMS.map(item => (
            <button key={item.id} style={styles.mobileSubItem} onClick={() => handleMenuClick(item.id)}>
              {item.name}
            </button>
          ))}

          <button style={styles.mobileTopButton} onClick={() => toggleMobileSection('solutions')}>
            <span>Solutions</span>
            <ChevronDown size={16} style={{ transform: mobileSection === 'solutions' ? 'rotate(180deg)' : 'none' }} />
          </button>
          {mobileSection === 'solutions' && SOLUTION_ITEMS.map(item => (
            <button key={item.id} style={styles.mobileSubItem} onClick={() => handleMenuClick(item.id)}>
              {item.name}
            </button>
          ))}

          <button style={styles.mobileTopButton} onClick={() => handleMenuClick('business')}>
            <span>Business Collaboration</span>
          </button>
          <button style={styles.mobileTopButton} onClick={() => handleMenuClick('registration')}>
            <span>Registration</span>
          </button>
          <button style={styles.mobileTopButton} onClick={() => handleMenuClick('about')}>
            <span>About</span>
          </button>

          <button style={styles.mobileContactButton} onClick={handleContactClick}>
            Contact
          </button>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
